/**
 * `branchesApi` — standalone RTK Query slice for ontology branch
 * management (the SettingsPage Branches card and the AppLayout branch
 * selector).
 *
 * Split off from `ontologyApi` so the branch surface owns its own
 * cache + tag space (`'Branches'`) instead of sharing the catch-all
 * `ontologyApi` tag soup. Shares `ontologyBaseQuery` (auth + branch
 * headers, `/api/v1/ontology` root) with `ontologyApi` /
 * `plantViewerApi` / `ingestApi` / the analysis slices via
 * `ontology-api-base.ts`.
 *
 * Everything rides REST: the proxy serves the branch domain model
 * directly (commit-backed branches — a branch is a pointer at a head
 * commit; the ConnectRPC branch service was removed with v1).
 *
 * Branch operations update the registry only; they don't mutate the
 * ontology graph. (`useSwitchBranch` fans out graph-cache invalidation
 * when the active branch changes.) `openBranch` is the one exception:
 * it materializes the branch's working database on first open (a
 * one-time ~seconds restore, idempotent afterwards).
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { ontologyBaseQuery } from '../core'
import { getApiErrorReason } from './branch-errors'
import type {
  Branch,
  BranchContext,
  BranchPage,
  Commit,
  CommitDetail,
  CommitPage,
  CompareResult,
  CreateBranchInput,
  CreateCommitInput,
  MergeBranchInput,
  MergeConflicts,
  MergeOutcome,
  MergePreviewResult,
  MergeRequestDetail,
  MergeRequestListItem,
  MergeRequestStatus,
  MergeResolutions,
  OpenMergeRequestInput,
  PatchMergeRequestInput,
  PrepareMergeResult,
  RenameBranchInput,
  RevertCommitInput,
  RevertOutcome,
  RestoreBranchInput,
  UpdateCommitMessageInput
} from './types'

export const BRANCHES_TAG_TYPES = ['Branches', 'Commits', 'MergeRequests'] as const
export type BranchesTagType = (typeof BRANCHES_TAG_TYPES)[number]

export interface ListBranchesArg {
  includeDeleted?: boolean
  origin?: 'user' | 'system'
  protected?: boolean
  offset?: number
  pageSize?: number
  q?: string
}

export const branchesApi = createApi({
  reducerPath: 'branchesApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: BRANCHES_TAG_TYPES,
  endpoints: (builder) => ({
    getContext: builder.query<BranchContext, void>({
      query: () => '/context'
    }),

    /**
     * Branches newest-first, offset-paginated: `offset`/`pageSize` select a
     * window, `origin` filters by population, `protected` (when set) filters on
     * the protected flag so the UI can list protected branches in their own
     * section, `q` filters by branch name, `totalSize` is the filtered total (so
     * a numbered pager can size itself). Each query tuple is its own cache entry
     * — numbered pages replace rather than accumulate. Tolerant of the legacy
     * bare-array proxy response (see `normalizeBranchPage`).
     */
    listBranches: builder.query<BranchPage, ListBranchesArg | void>({
      query: (arg) => {
        const q = arg?.q?.trim()
        return {
          url: '/branches',
          params: {
            ...(arg?.includeDeleted ? { include_deleted: 'true' } : {}),
            ...(arg?.origin ? { origin: arg.origin } : {}),
            ...(arg?.protected !== undefined ? { protected: String(arg.protected) } : {}),
            ...(arg?.offset ? { offset: arg.offset } : {}),
            ...(arg?.pageSize ? { page_size: arg.pageSize } : {}),
            ...(q ? { q } : {})
          }
        }
      },
      transformResponse: normalizeBranchPage,
      providesTags: ['Branches']
    }),
    createBranch: builder.mutation<Branch, CreateBranchInput>({
      query: ({ idempotencyKey, ...body }) => ({
        url: '/branches',
        method: 'POST',
        body,
        ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
      }),
      invalidatesTags: ['Branches']
    }),
    getBranch: builder.query<Branch, string>({
      query: (id) => `/branches/${encodeURIComponent(id)}`,
      providesTags: (_result, _error, id) => [{ type: 'Branches', id }]
    }),
    /**
     * The protected baseline `main` row, fetched by name via the collection
     * custom method `GET /branches:main`. The list is paginated newest-first and
     * main is the oldest row, so it can fall off the first page — callers that
     * need main (the picker root, the active-branch baseline) read it here
     * rather than scanning the list.
     */
    getMainBranch: builder.query<Branch, void>({
      query: () => '/branches:main',
      providesTags: [{ type: 'Branches', id: 'main' }]
    }),
    renameBranch: builder.mutation<Branch, RenameBranchInput>({
      query: ({ id, name }) => ({
        url: `/branches/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: { name }
      }),
      invalidatesTags: (_result, _error, { id }) => ['Branches', { type: 'Branches', id }]
    }),
    deleteBranch: builder.mutation<void, string>({
      query: (id) => ({
        url: `/branches/${encodeURIComponent(id)}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, id) => ['Branches', { type: 'Branches', id }]
    }),
    /**
     * Ensures the branch's working database exists, building it from the
     * branch's head commit on first open (one-time restore); idempotent and
     * near-instant afterwards. AIP-136 custom-method route (`:open`).
     */
    openBranch: builder.mutation<Branch, string>({
      query: (id) => ({
        url: `/branches/${encodeURIComponent(id)}:open`,
        method: 'POST'
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Branches', id }]
    }),
    /**
     * Freeze a branch: no direct graph writes, no commits, no rename or
     * delete — its state advances only via merges into it. Main is always
     * protected; any active branch can be protected too.
     */
    protectBranch: builder.mutation<Branch, string>({
      query: (id) => ({
        url: `/branches/${encodeURIComponent(id)}:protect`,
        method: 'POST'
      }),
      invalidatesTags: (_result, _error, id) => ['Branches', { type: 'Branches', id }]
    }),
    /** Lift the freeze. Refused for main, which is always protected. */
    unprotectBranch: builder.mutation<Branch, string>({
      query: (id) => ({
        url: `/branches/${encodeURIComponent(id)}:unprotect`,
        method: 'POST'
      }),
      invalidatesTags: (_result, _error, id) => ['Branches', { type: 'Branches', id }]
    }),
    /**
     * Rebuilds the branch's working database from a commit on its history:
     * the head by default ("discard uncommitted changes") or an ancestor
     * ("restore to this commit"). The head pointer never moves and committed
     * history is kept — committing afterwards records the removals as a new
     * commit. Refused for protected branches; 409 COMMIT_NOT_ON_BRANCH for
     * commits outside the branch's own line. The cache-coherence bridge
     * refreshes graph-derived views after a successful restore.
     */
    restoreBranch: builder.mutation<Branch, RestoreBranchInput>({
      query: ({ id, commit, idempotencyKey }) => ({
        url: `/branches/${encodeURIComponent(id)}:restore`,
        method: 'POST',
        body: commit ? { commit } : {},
        ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {})
      }),
      invalidatesTags: (_result, _error, { id }) => ['Branches', { type: 'Branches', id }]
    }),

    /**
     * Records the branch's working database as a new commit and advances the
     * head. Synchronous and scan-bound on large graphs — callers own the
     * waiting state.
     */
    createCommit: builder.mutation<Commit, CreateCommitInput>({
      query: ({ branchId, message }) => ({
        url: `/branches/${encodeURIComponent(branchId)}/commits`,
        method: 'POST',
        body: message ? { message } : {}
      }),
      invalidatesTags: ['Commits', 'Branches']
    }),
    /**
     * First-parent commit history from the branch head, newest first. One
     * cache entry per branch: pages merge in (deduped by hash, so an
     * invalidation refetch cannot double rows), `pageToken` walks forward.
     */
    listCommits: builder.query<CommitPage, { branchId: string; pageToken?: string }>({
      query: ({ branchId, pageToken }) => ({
        url: `/branches/${encodeURIComponent(branchId)}/commits`,
        params: pageToken ? { page_token: pageToken } : undefined
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}(${queryArgs.branchId})`,
      merge: (current, incoming, { arg }) => {
        const base = arg.pageToken ? current.commits : []
        const seen = new Set(base.map((c) => c.hash))
        return {
          commits: [...base, ...incoming.commits.filter((c) => !seen.has(c.hash))],
          nextPageToken: incoming.nextPageToken
        }
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.pageToken !== previousArg?.pageToken,
      providesTags: ['Commits']
    }),
    /** One commit's metadata plus its stored delta payload (raw; decode at render). */
    getCommit: builder.query<CommitDetail, string>({
      query: (hash) => `/commits/${encodeURIComponent(hash)}`,
      providesTags: (_result, _error, hash) => [{ type: 'Commits', id: hash }]
    }),
    /** Replaces only the commit's message; hash and graph content stay unchanged. */
    updateCommitMessage: builder.mutation<Commit, UpdateCommitMessageInput>({
      query: ({ hash, message }) => ({
        url: `/commits/${encodeURIComponent(hash)}`,
        method: 'PATCH',
        body: { message }
      }),
      invalidatesTags: (_result, _error, { hash }) => ['Commits', { type: 'Commits', id: hash }]
    }),

    /**
     * Merges source into target. The API's 409 MERGE_CONFLICTS is unwrapped
     * here into the `MergeOutcome` union — conflicts are data the resolver
     * renders, never an RTK error — while every other failure stays an error.
     * Synchronous and scan-bound on large graphs; callers own the waiting
     * state. Invalidates only when something was actually merged.
     */
    mergeBranch: builder.mutation<MergeOutcome, MergeBranchInput>({
      queryFn: async (
        { targetId, sourceId, message, resolutions, stageHash },
        _api,
        _extra,
        baseQuery
      ) => {
        const res = await baseQuery({
          url: `/branches/${encodeURIComponent(targetId)}:merge`,
          method: 'POST',
          body: {
            sourceId,
            ...(message ? { message } : {}),
            ...(resolutions ? { resolutions } : {}),
            ...(stageHash ? { stageHash } : {})
          }
        })
        return unwrapMergeResponse(res, sourceId)
      },
      invalidatesTags: (result) => (result?.status === 'merged' ? ['Branches', 'Commits'] : [])
    }),
    /**
     * Applies one commit's inverse as a new forward commit on the same branch.
     * Conflict 409s reuse merge's payload and are unwrapped as data.
     */
    revertCommit: builder.mutation<RevertOutcome, RevertCommitInput>({
      queryFn: async ({ branchId, commit, message, resolutions }, _api, _extra, baseQuery) => {
        const res = await baseQuery({
          url: `/branches/${encodeURIComponent(branchId)}:revertCommit`,
          method: 'POST',
          body: {
            commit,
            ...(message ? { message } : {}),
            ...(resolutions ? { resolutions } : {})
          }
        })
        return unwrapRevertResponse(res)
      },
      invalidatesTags: (result) => (result?.status === 'reverted' ? ['Branches', 'Commits'] : [])
    }),
    /**
     * The pre-merge review: the two-way diff of the source relative to the
     * target, reading each side's working DB when open (uncommitted work
     * included). Entity props arrive type-tagged; decode at render.
     */
    compareBranches: builder.query<CompareResult, { targetId: string; sourceId: string }>({
      query: ({ targetId, sourceId }) => ({
        url: `/branches/${encodeURIComponent(targetId)}:compare`,
        params: { source: sourceId }
      }),
      providesTags: ['Branches', 'Commits']
    }),
    /**
     * Non-destructive 3-way merge preview: reads the current live target and
     * source states, including uncommitted work, and returns conflicts without
     * auto-committing or applying anything.
     */
    previewMerge: builder.query<MergePreviewResult, { targetId: string; sourceId: string }>({
      query: ({ targetId, sourceId }) => ({
        url: `/branches/${encodeURIComponent(targetId)}:mergePreview`,
        params: { source: sourceId }
      }),
      providesTags: ['Branches', 'Commits']
    }),
    /**
     * The preview that stages: computes the plan against the live databases
     * first and only commits both dirty sides (pinning the inputs to immutable
     * heads) and stages the merged delta — returning a `stageHash` that
     * `mergeBranch` applies without recomputing — when the merge is conflict-free
     * AND brings in real changes. With `noChanges` nothing is committed. Because
     * it can advance the committed heads, it invalidates the branch/commit caches.
     */
    prepareMerge: builder.mutation<
      PrepareMergeResult,
      { targetId: string; sourceId: string; resolutions?: MergeResolutions }
    >({
      query: ({ targetId, sourceId, resolutions }) => ({
        url: `/branches/${encodeURIComponent(targetId)}:prepareMerge`,
        method: 'POST',
        body: { sourceId, ...(resolutions ? { resolutions } : {}) }
      }),
      invalidatesTags: ['Branches', 'Commits']
    }),

    /**
     * The "merges in progress" list, newest-updated first, optionally filtered
     * by status. Each row carries the source/target names and a resolved count.
     */
    listMergeRequests: builder.query<
      MergeRequestListItem[],
      { status?: MergeRequestStatus } | void
    >({
      query: (arg) => ({
        url: '/mergeRequests',
        params: arg?.status ? { status: arg.status } : undefined
      }),
      transformResponse: (resp: { mergeRequests?: MergeRequestListItem[] }) =>
        resp.mergeRequests ?? [],
      providesTags: ['MergeRequests']
    }),
    /**
     * One durable merge request: the persisted resolutions verbatim plus
     * `headDrift` (a side advanced since the session was pinned). The fresh
     * conflict set is read separately via `previewMerge`.
     */
    getMergeRequest: builder.query<MergeRequestDetail, string>({
      query: (id) => `/mergeRequests/${encodeURIComponent(id)}`,
      providesTags: (_result, _error, id) => [{ type: 'MergeRequests', id }]
    }),
    /**
     * Enqueue a background preview for the pair (202 Accepted). Returns the
     * queued merge-request detail immediately; terminal conflicts / incoming /
     * noChanges land on the request and are discovered when merge wake-ups
     * refetch GET detail or the list. Invalidates only MergeRequests on
     * accept — Branches/Commits refresh when the terminal observer sees
     * preview success (Prepare may auto-commit).
     */
    openMergeRequest: builder.mutation<MergeRequestDetail, OpenMergeRequestInput>({
      query: ({ targetId, sourceId, title }) => ({
        url: `/branches/${encodeURIComponent(targetId)}:mergeRequest`,
        method: 'POST',
        body: { sourceId, ...(title ? { title } : {}) }
      }),
      invalidatesTags: ['MergeRequests']
    }),
    /**
     * Autosave the in-progress resolutions (debounced by the caller). Invalidates
     * the list too so the "merges in progress" page's resolved/total count
     * reflects new picks without a manual refresh.
     */
    patchMergeRequest: builder.mutation<MergeRequestDetail, PatchMergeRequestInput>({
      query: ({ id, resolutions }) => ({
        url: `/mergeRequests/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: { resolutions }
      }),
      invalidatesTags: (_result, _error, { id }) => ['MergeRequests', { type: 'MergeRequests', id }]
    }),
    /** Abandon a request. */
    closeMergeRequest: builder.mutation<MergeRequestDetail, string>({
      query: (id) => ({
        url: `/mergeRequests/${encodeURIComponent(id)}:close`,
        method: 'POST'
      }),
      invalidatesTags: (_result, _error, id) => ['MergeRequests', { type: 'MergeRequests', id }]
    }),
    /**
     * Enqueue a background apply (202 Accepted). Returns the queued detail;
     * merged/conflicts/failure outcomes are persisted on the request and
     * discovered on merge wake-ups. Invalidates only MergeRequests on accept —
     * Branches/Commits/graph refresh when the terminal observer sees apply
     * success.
     */
    mergeMergeRequest: builder.mutation<MergeRequestDetail, { id: string; message?: string }>({
      query: ({ id, message }) => ({
        url: `/mergeRequests/${encodeURIComponent(id)}:merge`,
        method: 'POST',
        body: message ? { message } : {}
      }),
      invalidatesTags: ['MergeRequests']
    })
  })
})

/**
 * Tolerant reader for GET /branches: the new proxy returns the paginated
 * envelope `{ branches, totalSize }`; the legacy proxy returns a bare
 * `Branch[]`. A bare array is wrapped into an envelope with `totalSize` set to
 * its own length. Exported for direct unit testing.
 */
export function normalizeBranchPage(resp: unknown): BranchPage {
  if (Array.isArray(resp)) {
    return { branches: resp as Branch[], totalSize: resp.length }
  }
  return resp as BranchPage
}

/**
 * Converts the raw merge response into the `MergeOutcome` union: 200 →
 * merged, 409 MERGE_CONFLICTS → conflicts (data), anything else → the error.
 * Exported for direct unit testing.
 */
export function unwrapMergeResponse(
  res: { data?: unknown; error?: unknown },
  sourceId: string
): { data: MergeOutcome } | { error: FetchBaseQueryError } {
  if (res.error) {
    if (getApiErrorReason(res.error) === 'MERGE_CONFLICTS') {
      const details = (res.error as { data?: { details?: MergeConflicts } }).data?.details
      return {
        data: {
          status: 'conflicts',
          conflicts: details ?? { nodes: [], rels: [] }
        }
      }
    }
    return { error: res.error as FetchBaseQueryError }
  }
  const body = res.data as { commit: Commit }
  return {
    data: {
      status: 'merged',
      commit: body.commit,
      sourceId
    }
  }
}

export function unwrapRevertResponse(res: {
  data?: unknown
  error?: unknown
}): { data: RevertOutcome } | { error: FetchBaseQueryError } {
  if (res.error) {
    const conflicts = unwrapMergeConflicts(res.error)
    if (conflicts) return { data: { status: 'conflicts', conflicts } }
    return { error: res.error as FetchBaseQueryError }
  }
  const body = res.data as { branch: Branch; commit: Commit }
  return {
    data: {
      status: 'reverted',
      branch: body.branch,
      commit: body.commit
    }
  }
}

function unwrapMergeConflicts(error: unknown): MergeConflicts | undefined {
  if (getApiErrorReason(error) !== 'MERGE_CONFLICTS') return undefined
  return (
    (error as { data?: { details?: MergeConflicts } }).data?.details ?? {
      nodes: [],
      rels: []
    }
  )
}

export const {
  useGetContextQuery,
  useListBranchesQuery,
  useCreateBranchMutation,
  useGetBranchQuery,
  useGetMainBranchQuery,
  useRenameBranchMutation,
  useDeleteBranchMutation,
  useOpenBranchMutation,
  useProtectBranchMutation,
  useUnprotectBranchMutation,
  useRestoreBranchMutation,
  useCreateCommitMutation,
  useListCommitsQuery,
  useGetCommitQuery,
  useUpdateCommitMessageMutation,
  useMergeBranchMutation,
  useRevertCommitMutation,
  useCompareBranchesQuery,
  usePreviewMergeQuery,
  usePrepareMergeMutation,
  useListMergeRequestsQuery,
  useGetMergeRequestQuery,
  useOpenMergeRequestMutation,
  usePatchMergeRequestMutation,
  useCloseMergeRequestMutation,
  useMergeMergeRequestMutation
} = branchesApi
